import { sendTractiveMetric } from '@qualtrics/page-metrics/lib/tractiveService';
import SessionStorageManager from './session-storage-manager';
import cookies from './cookies';
import http from './http';
import { globalWrapperCache } from './global-wrapper-cache';
import { isPathAvailableInGivenPortal } from '@qualtrics-portals/portal-checker';
import { filterAsync } from '../../utils/filterAsync';
import { getDashboardType } from './dashboards-service';
import { isExportPage } from './tool-check';
import { ensurePortalMode, setPortalMode } from './portal-mode-service';

const cache = new SessionStorageManager(window);
const portalIdCacheKey = (sessionId) => `portal_id_${sessionId}`;
const buildPortalCacheValue = (portalId, instanceId) => {
  if (instanceId) {
    return `${portalId}+${instanceId}`;
  }
  return portalId;
};

export const QUALTRICS_ID = 'Qualtrics';
const PARTICIPANT_PORTAL_PAGE_ID = 'ParticipantPortal';
const PARTICIPANT_PORTAL_FAM_PAGE_ID = 'ParticipantPortalFam';
const PARTICIPANT_PORTAL_PAGE_IDS = [
  PARTICIPANT_PORTAL_PAGE_ID,
  PARTICIPANT_PORTAL_FAM_PAGE_ID,
];
const DX_PORTAL_ID = 'DXPortal';
const DX_PORTAL_PBA_ID = 'dx-portal';
const INSIGHTS_EXPLORER_PORTAL_ID = 'InsightExplorer';

/**
 * @param {V2InfoContextType} v2InfoContext
 * @returns boolean
 */
export const isPortalsApp = (v2InfoContext) => {
  return v2InfoContext?.contextType === 'PortalsApp';
};

/**
 * @param {V2InfoContextType} v2InfoContext
 * @returns boolean
 */
export const isParticipantPortalApp = (v2InfoContext) => {
  return v2InfoContext?.contextType === PARTICIPANT_PORTAL_PAGE_ID;
};

/**
 * @param {V2InfoContextType} v2InfoContext
 * @returns boolean
 */
export const isParticipantPortalFamApp = (v2InfoContext) => {
  return v2InfoContext?.contextType === PARTICIPANT_PORTAL_FAM_PAGE_ID;
};

/**
 * @param {V2InfoContextType} v2InfoContext
 * @returns boolean
 */
export const isInsightsExplorerApp = (v2InfoContext) => {
  return v2InfoContext?.contextType === 'InsightExplorerApp';
};

/**
 * @param {V2InfoContextType} v2InfoContext
 * @returns boolean
 */
export const isDXPortalApp = (v2InfoContext) => {
  return v2InfoContext?.contextType === DX_PORTAL_ID;
};

/**
 * @param {V2InfoContextType} v2InfoContext
 * @returns boolean
 */
export const isPortalsAppChild = (v2InfoContext) => {
  try {
    return (
      !isPortalsApp(v2InfoContext) &&
      window.parent.Q_PortalsContext !== undefined
    );
  } catch {
    // in case we're inside an iframe with cross-origin blocked and we cannot access parent
    return false;
  }
};

/**
 * @param {string} pageId
 * @returns boolean
 */
export const isParticipantPortalPage = (pageId) => {
  return pageId === PARTICIPANT_PORTAL_PAGE_ID;
};

/**
 * Retrieves the user's favorite PBA from user settings
 * @returns {Promise<{id: string, instanceId?: string}|null>} Promise that resolves to the favorite PBA object or null if not set
 */
export const getFavoritePba = () => {
  const sessionId = cookies.get('UDSSessionKey');
  const cacheKey = `/portals/api/user-settings/favoritePba_${sessionId}`;
  const cachedData = cache.getSessionStorage(cacheKey);

  if (cachedData) {
    try {
      return Promise.resolve(JSON.parse(cachedData));
    } catch {
      cache.removeSessionStorage(cacheKey);
    }
  }

  const response = http.getJSON('/portals/api/user-settings');
  return response.then((data) => {
    const favoritePba = data?.favoritePba;

    if (!favoritePba) {
      cache.setSessionStorage(cacheKey, JSON.stringify(null));
      return null;
    }

    // Parse favoritePba string into portal object { id, instanceId }
    const parts = favoritePba.split(':');
    const portalObject = {
      id: parts[0],
      instanceId: parts[1] ?? null,
    };

    cache.setSessionStorage(cacheKey, JSON.stringify(portalObject));
    return portalObject;
  });
};

/**
 * Sets or removes the user's favorite PBA in user settings
 * @param {string|null} pbaId - The ID of the PBA to set as favorite, or null to remove the favorite PBA
 * @param {string|null} [instanceId] - Optional instance ID to append to the PBA ID
 * @returns {Promise<{success: boolean, data?: any, error?: string, status?: number, transactionId?: string}>} Promise that resolves with the operation result
 */
export const setFavoritePba = (pbaId = null, instanceId = null) => {
  return http
    .patchJSON('/portals/api/user-settings', {
      favoritePba: pbaId
        ? `${pbaId}${instanceId ? `:${instanceId}` : ''}`
        : null,
    })
    .then((data) => {
      const sessionId = cookies.get('UDSSessionKey');
      const cacheKey = `/portals/api/user-settings/favoritePba_${sessionId}`;
      const newValue = pbaId ? { id: pbaId, instanceId: instanceId } : null;
      cache.setSessionStorage(cacheKey, JSON.stringify(newValue));
      return {
        success: true,
        data: data,
      };
    })
    .catch((error) => {
      return {
        success: false,
        error: error.statusText || 'Unknown error occurred',
        status: error.status,
        transactionId: error.transactionId,
      };
    });
};

/**
 * @param  {V2InfoContextType} v2InfoContext
 * @param {boolean} isLegacyPortalHeader
 * @param {string} pageId
 * @returns {{currentPortalId?: string, isParticipantPortal: boolean, isParticipantPortalInProject: boolean, isPortalsApplication: boolean, isPortalsChild: boolean}}
 */
export const getPortalInfo = (v2InfoContext, isLegacyPortalHeader, pageId) => {
  const isPortalsAppContext = isPortalsApp(v2InfoContext);

  const isParticipantPortalContext =
    isParticipantPortalApp(v2InfoContext) || isLegacyPortalHeader;

  const isParticipantPortalFamContext =
    isParticipantPortalFamApp(v2InfoContext);

  const isParticipantPortalInProject =
    isParticipantPortalPage(pageId) &&
    !isParticipantPortalContext &&
    !isParticipantPortalFamContext;

  const isIframeInsidePortal = isPortalsAppChild(v2InfoContext);

  let currentPortalId = QUALTRICS_ID;
  let currentInstanceId;
  if (isPortalsAppContext) {
    currentPortalId = v2InfoContext?.resourceId;
    currentInstanceId = v2InfoContext?.subresourceId;
  } else if (isParticipantPortalContext || isParticipantPortalInProject) {
    currentPortalId = PARTICIPANT_PORTAL_PAGE_ID;
  } else if (isParticipantPortalFamContext) {
    currentPortalId = PARTICIPANT_PORTAL_FAM_PAGE_ID;
  } else if (isDXPortalApp(v2InfoContext)) {
    currentPortalId = DX_PORTAL_PBA_ID;
  } else if (isInsightsExplorerApp(v2InfoContext)) {
    currentPortalId = INSIGHTS_EXPLORER_PORTAL_ID;
  }

  return {
    isPortalsApplication: isPortalsAppContext,
    isPortalsChild: isIframeInsidePortal,
    isParticipantPortal:
      isParticipantPortalContext || isParticipantPortalFamContext,
    isParticipantPortalInProject,
    currentPortalId,
    currentInstanceId,
  };
};

export const notifyAboutContext = (ctx, activeSuperTab) => {
  if (isPortalsAppChild(ctx) && window.parent.Q_PortalsContext.onContextLoad) {
    window.parent.Q_PortalsContext.onContextLoad(ctx, activeSuperTab);
  }
};

export const getPortalIds = (ctx) => {
  if (isPortalsApp(ctx)) {
    return {
      portalId: ctx.resourceId,
      instanceId: ctx.subresourceId,
    };
  }
  if (isPortalsAppChild(ctx)) {
    return {
      portalId: window.parent.Q_PortalsContext.portalId,
      instanceId: window.parent.Q_PortalsContext.instanceId,
    };
  }
  return {
    portalId: undefined,
    instanceId: undefined,
  };
};

export const getAvailablePortals = async (language) => {
  const portalsUrl = `/portals/api/available-portals?includeURLLists=true&lang=${language}&includeExternalPortals=true`;
  const sessionId = cookies.get('UDSSessionKey');
  const key = `${portalsUrl}&UDSSessionKey=${sessionId}`;
  const cacheData = cache.getSessionStorage(key);
  if (cacheData) {
    return JSON.parse(cacheData).portals;
  } else {
    const availablePortals = http.getJSON(portalsUrl);
    return availablePortals
      .then((data) => {
        if (!data || !data.portals) {
          return [];
        }
        cache.setSessionStorage(key, JSON.stringify(data));
        return data.portals;
      })
      .catch((res) => {
        let message = 'Cannot get available portals';
        if (res) {
          message = res.status + ' ' + res.statusText;
        }
        console.error(message);
        return [];
      });
  }
};

export const storePortalIds = (portalId, instanceId) => {
  const sessionId = cookies.get('UDSSessionKey');
  const portalCacheValue = buildPortalCacheValue(portalId, instanceId);
  cache.setSessionStorage(portalIdCacheKey(sessionId), portalCacheValue);
  if (portalId) {
    // Update synchronously so any same-session read (e.g. usePortalsInfo)
    // immediately sees the new value, mirroring the old cookie's
    // read-after-write behavior instead of waiting on the POST below.
    setPortalMode({ portalId, instanceId });
    fetch('/wrapper/v1/portal-mode', {
      method: 'POST',
      body: JSON.stringify({ portalId, instanceId }),
      headers: { 'Content-Type': 'application/json' },
      keepalive: true,
      credentials: 'include',
    }).catch(() => {});
  }
};

export const getStoredPortalIds = () => {
  const sessionId = cookies.get('UDSSessionKey');
  const cacheValue = cache.getSessionStorage(portalIdCacheKey(sessionId)) || '';
  const valueParts = cacheValue.split('+');
  return {
    portalId: valueParts[0],
    instanceId: valueParts[1],
  };
};

export const switchPortalFromMenu = (
  targetPortalId,
  targetInstanceId,
  currentPortalId,
) => {
  sendTractiveMetric({
    billingTeam: 'EUA',
    eventName: 'App.Switch',
    customAttributes: {
      portalId: currentPortalId,
      switchTo: targetPortalId,
    },
  });
  storePortalIds(targetPortalId, targetInstanceId);
};

export const emitAppLoadEvent = (portalId, portals) => {
  const { portalId: currentPortalId } = getStoredPortalIds();
  if (currentPortalId !== window.parent.Q_PortalsContext?.portalId) {
    // if config not loaded yet, try again in 100ms (config is needed to define if custom logo and theme used)
    // eslint-disable-next-line angular/timeout-service
    return setTimeout(() => emitAppLoadEvent(portalId, portals), 100);
  }
  if (portalId !== 'Qualtrics') {
    const availablePortalIds = portals
      ? portals.map((portal) => portal.id)
      : [];
    sendTractiveMetric({
      billingTeam: 'EUA',
      eventName: 'App.Load',
      customAttributes: {
        portalId: portalId,
        availablePortalIds,
        hasCustomLogo: !!window.parent.Q_PortalsContext?.portalData?.logoUrl,
        hasCustomTheme:
          !!window.parent.Q_PortalsContext?.portalData?.headerColor,
      },
    });
  }
};
export const getSwitchPortalUrl = (portal, currentPortalId, portals) => {
  if (portal.portalType === 'EXTERNAL') {
    return portal.homeHref;
  }

  if (
    portal.id === QUALTRICS_ID &&
    PARTICIPANT_PORTAL_PAGE_IDS.includes(currentPortalId) &&
    !portals.find((portal) => PARTICIPANT_PORTAL_PAGE_IDS.includes(portal.id))
  ) {
    // when switching to qualtrics from participant portal when there's no participant portal available
    // force a switch to homepage
    return `/portals/api/switch-portal?portalId=${portal.id}`;
  }
  return getRedirectPortalUrl(portal.id, portal.instanceId);
};

export const getRedirectPortalUrl = (portalId, instanceId, path) => {
  const link = window.location.href;
  const pathParam = path || link.replace(window.location.origin, '');
  const encodedPathParamPart = pathParam
    ? `&path=${encodeURIComponent(pathParam)}`
    : '';
  let redirectUrl = `/portals/api/switch-portal?portalId=${portalId}${encodedPathParamPart}`;
  if (instanceId) {
    redirectUrl += `&instanceId=${instanceId}`;
  }
  return redirectUrl;
};

export const redirectToPortal = (portalId, instanceId, path, userId) => {
  storePortalIds(portalId, instanceId);
  window.location.href = getRedirectPortalUrl(portalId, instanceId, path);
};

export const selectPortalAndRedirect = (portalsValidForPath, path, userId) => {
  const selectedPortal = portalsValidForPath[0];

  if (selectedPortal.id === QUALTRICS_ID) {
    storePortalIds(QUALTRICS_ID, undefined);
  } else {
    redirectToPortal(
      selectedPortal.id,
      selectedPortal.instanceId,
      path,
      userId,
    );
  }
};

const portalMatchesIds = (portal, portalId, instanceId) => {
  if (portal.id !== portalId) {
    return false;
  }
  if (instanceId) {
    return portal.instanceId === instanceId;
  }
  return true;
};

export const checkIfPortalIsValidAndRedirectIfNeeded = (
  portalIdToCheck,
  instanceIdToCheck,
  portalsValidForPath,
  path,
  userId,
  isPortalsRoadblockEnabled = false,
) => {
  if (
    portalsValidForPath.some((portal) =>
      portalMatchesIds(portal, portalIdToCheck, instanceIdToCheck),
    )
  ) {
    if (portalIdToCheck === QUALTRICS_ID) {
      storePortalIds(QUALTRICS_ID, undefined);
    } else {
      globalWrapperCache.removeAllPortalConnectedItems();
      redirectToPortal(portalIdToCheck, instanceIdToCheck, path, userId);
    }
    return;
  }
  if (isPortalsRoadblockEnabled && portalsValidForPath.length > 1) {
    window.location.replace(
      `/portals/chooser/ui?path=${encodeURIComponent(
        path,
      )}&displayRoadblock=true`,
    );
  } else {
    selectPortalAndRedirect(portalsValidForPath, path, userId);
  }
};

export const checkIfPageIsValidInPortal = async (
  portals,
  context,
  userId,
  isRestrictedNavigation,
  pageId,
  isPortalsRoadblockEnabled,
  isBigQualtricsFallbackBlocked,
) => {
  const portalMode = await ensurePortalMode();
  const portalIdFromCookie = portalMode?.portalId;

  if (
    (context.contextType === 'AccountSettings' ||
      context.contextType === 'AccountSettingsV2') &&
    isBigQualtricsFallbackBlocked
  ) {
    return;
  }

  const isBigQualtricsAvailable =
    portals.some((portal) => portal.id === QUALTRICS_ID) ||
    !isBigQualtricsFallbackBlocked;

  if (
    isRestrictedNavigation ||
    !portals ||
    (!portalIdFromCookie && isBigQualtricsAvailable)
  ) {
    // shared user account, no portals support for them
    return;
  }
  if (isPortalsAppChild(context)) {
    // no action required for portals frames
    return;
  }
  if (
    isParticipantPortalApp(context) ||
    isParticipantPortalFamApp(context) ||
    isParticipantPortalPage(pageId)
  ) {
    // for now treat ParticipantPortal as a special case of portal and don't redirect away from it in any case
    return;
  }
  if (isDXPortalApp(context)) {
    // for now treat DXPortal as a special case of portal and don't redirect away from it in any case
    return;
  }
  if (isExportPage()) {
    // export page should always be open in BigQualtrics mode as it's settings fields on window
    return;
  }
  if (isInsightsExplorerApp(context)) {
    // for now treat InsightsExplorer as a special case of portal and don't redirect away from it in any case
    return;
  }
  if (isPortalsApp(context)) {
    // portals app, store current portal id
    const { portalId, instanceId } = getPortalIds(context);
    storePortalIds(portalId, instanceId);
    emitAppLoadEvent(portalId, portals);
    return;
  }
  const fullPath = window.location.href.replace(window.location.origin, '');

  const portalsValidForPath = await filterAsync(
    portals,
    async (portal) =>
      portal.portalType !== 'EXTERNAL' &&
      (await isPathAvailableInGivenPortal(portal, fullPath, (dashboardId) =>
        getDashboardType(dashboardId, cache),
      )),
  );

  if (portalsValidForPath.length === 0) {
    const { portalId: storedPortalId } = getStoredPortalIds();
    if (!isBigQualtricsAvailable) {
      window.location.replace('/portals/chooser/ui');
    } else {
      sendTractiveMetric({
        billingTeam: 'EUA',
        eventName: 'App.BigQRedirect',
        customAttributes: {
          action: 'redirect',
          portalId: storedPortalId,
          destinationPath: fullPath,
        },
      });
    }
    return;
  }
  const { portalId: storedPortalId, instanceId: storedInstanceId } =
    getStoredPortalIds();
  if (storedPortalId) {
    checkIfPortalIsValidAndRedirectIfNeeded(
      storedPortalId,
      storedInstanceId,
      portalsValidForPath,
      fullPath,
      userId,
      isPortalsRoadblockEnabled,
    );
  } else {
    checkIfPortalIsValidAndRedirectIfNeeded(
      portalMode?.portalId || QUALTRICS_ID,
      portalMode?.instanceId,
      portalsValidForPath,
      fullPath,
      userId,
      isPortalsRoadblockEnabled,
    );
  }
};
